import React, { useEffect, useRef, useState } from "react";
import Icons from "../icons";
import { useTheme } from "../theme";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ProfileMenu({ onLogout, onOpenSettings }) {
  const { mode, color, palettes, setMode, setColor, username, email, userkey } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = getInitials(username);
  const [savedSettings, setSavedSettings] = useState({ mode, color });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const isDirty = mode !== savedSettings.mode || color !== savedSettings.color;

  const handleSaveColor = async () => {
    if (!userkey) {
      setSaveMsg("Not signed in.");
      return;
    }
    setSaving(true);
    setSaveMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}userpreferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userkey,
          pagename: "main",
          settings: { mode, color, theme: color },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveMsg(body?.detail || "Failed to save.");
        return;
      }
      setSavedSettings({ mode, color });
      setSaveMsg("Saved");
    } catch {
      setSaveMsg("Network error.");
    } finally {
      setSaving(false);
    }
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {/* Avatar button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/25"
      >
        {initials}
      </button>

      {/* Popover */}
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-card shadow-xl ring-1 ring-black/5"
        >
          {/* Account header */}
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {username || "Guest"}
              </p>
              <p className="truncate text-xs text-slate-500">{email || "—"}</p>
            </div>
          </div>

          {/* Appearance */}
          <div className="px-4 py-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Appearance
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "light", label: "Light", icon: Icons.Sun },
                { id: "dark", label: "Dark", icon: Icons.Moon },
              ].map((opt) => {
                const OptIcon = opt.icon;
                const active = mode === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setMode(opt.id)}
                    className={[
                      "flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition",
                      active
                        ? "border-brand-500 bg-brand-50 text-brand-700"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <OptIcon className="h-4 w-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Accent color */}
          <div className="px-4 pb-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Accent color
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(palettes).map(([key, p]) => {
                const active = color === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setColor(key)}
                    title={p.label}
                    aria-label={`${p.label} accent`}
                    aria-pressed={active}
                    className={[
                      "flex h-8 w-8 items-center justify-center rounded-full ring-2 ring-offset-2 ring-offset-card transition",
                      active ? "ring-slate-400" : "ring-transparent hover:ring-slate-200",
                    ].join(" ")}
                    style={{ backgroundColor: p.swatch }}
                  >
                    {active && <Icons.Check className="h-4 w-4 text-white" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <span
                className={[
                  "text-xs",
                  saveMsg === "Saved" ? "text-emerald-600" : "text-red-600",
                ].join(" ")}
              >
                {saveMsg}
              </span>
              <button
                type="button"
                onClick={handleSaveColor}
                disabled={saving || !isDirty}
                className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>

          {/* Sign out */}
          <div className="border-t border-slate-100 p-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenSettings?.();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <Icons.Settings className="h-4 w-4 text-slate-400" />
              Settings
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout?.();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <Icons.LogOut className="h-4 w-4 text-slate-400" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
